import { useWalletBreakdown } from '../hooks/useWalletBreakdown';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownLeft,
  Bitcoin,
  DollarSign,
  CreditCard,
  Banknote,
  AlertTriangle,
  CheckCircle,
  Clock,
  Copy,
  Eye,
  EyeOff,
  Shield,
  Info,
  Layers,
  CreditCard as Card,
  ArrowRight,
  Landmark,
  BarChart3
} from 'lucide-react';
import NowPaymentsDeposit from './NowPaymentsDeposit';
import BankWithdrawalModal from './BankWithdrawalModal';
import CryptoWithdrawalModal from './CryptoWithdrawalModal';
import { Transaction } from '../App';
import CryptoHoldings from './CryptoHoldings';
import PnlStatement from './PnlStatement';
import { MarketData, DatabaseUserAsset } from '../hooks/useDatabase';
import { useDatabase } from '../hooks/useDatabase';
import { supabase } from '../lib/supabaseClient';
import WalletBreakdownCard from './WalletBreakdownCard';
import { useMarketData } from '../contexts/MarketDataContext';
import { useBybitData } from '../contexts/BybitDataContext';

interface WalletBreakdown {
  totalBalance: number;
  usedMargin: number;
  unrealizedPnl: number;
  availableBalance: number;
  reserved: number;
}

interface WalletPageProps {
  usdtBalance: number;
  btcBalance: number;
  transactions: Transaction[];
  kycStatus: 'not_verified' | 'pending' | 'verified';
  setTradingMode?: (mode: string) => void;
  marketData: MarketData[];
  userAssets?: DatabaseUserAsset[];
  onWalletOperation?: (type: string, currency: string, amount: number) => Promise<void>;
  totalPortfolioValue: number;
  updateBalances?: (balances: any) => Promise<void>;
  addTransaction?: (transaction: any) => Promise<void>;
  setShowWithdrawalModal?: (show: boolean) => void;
  showWithdrawalModal?: boolean;
  walletBreakdown?: WalletBreakdown;
  currentBtcPrice?: number;
}

const WalletPage: React.FC<WalletPageProps> = ({
  usdtBalance,
  btcBalance,
  kycStatus,
  setTradingMode,
  marketData,
  userAssets = [],
  onWalletOperation,
  totalPortfolioValue, // Destructure the prop
  setShowWithdrawalModal,
  showWithdrawalModal,
  currentBtcPrice,
  walletBreakdown
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userStakes, calculateCurrentEarnings, cancelUserStake, robotState } = useDatabase();
  const { transactions, addTransaction, updateBalances } = useDatabase();
  const { marketData: contextMarketData, snapshotData, getSnapshotPriceBySymbol } = useMarketData();
  const { getPriceBySymbol: getBybitPrice } = useBybitData();

  // Helper function to format date safely
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };
  // State for wallet operations
  const [activeTab, setActiveTab] = useState('overview');
  const [depositMethod, setDepositMethod] = useState<'bank_transfer' | 'nowpayments' | 'btc_direct'>('bank_transfer');
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<'USDT' | 'BTC'>('USDT');
  const [showBalance, setShowBalance] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [showBankWithdrawalModal, setShowBankWithdrawalModal] = useState(false);
  const [showCryptoWithdrawalModal, setShowCryptoWithdrawalModal] = useState(false);
  const [selectedCryptoForWithdrawal, setSelectedCryptoForWithdrawal] = useState<'USDT' | 'BTC'>('USDT');
  
  // Get the actual BTC price to use (moved here so it can be used in hooks)
  const actualBtcPrice = useMemo(() => {
    // Strategy 1: Try websocket data first (most real-time)
    const bybitPrice = getBybitPrice('BTCUSDT');
    if (bybitPrice > 0) {
      return bybitPrice;
    }

    // Strategy 2: Try snapshot data (stable fallback)
    const snapshotPrice = getSnapshotPriceBySymbol('BTCUSDT');
    if (snapshotPrice > 0) {
      return snapshotPrice;
    }

    // Strategy 3: Try direct contextMarketData lookup
    const marketDataItem = contextMarketData.find(item => item.symbol === 'BTCUSDT');
    if (marketDataItem && marketDataItem.price > 0) {
      return marketDataItem.price;
    }

    // Strategy 4: Try snapshotData direct lookup
    const snapshotItem = snapshotData.find(item => item.symbol === 'BTCUSDT');
    if (snapshotItem && snapshotItem.price > 0) {
      return snapshotItem.price;
    }

    // Strategy 5: Try prop marketData
    const propMarketDataItem = marketData.find(item => item.symbol === 'BTCUSDT');
    if (propMarketDataItem && propMarketDataItem.price > 0) {
      return propMarketDataItem.price;
    }

    // Strategy 6: Use prop as final fallback
    if (currentBtcPrice && currentBtcPrice > 0) {
      return currentBtcPrice;
    }

    return 0;
  }, [getBybitPrice, getSnapshotPriceBySymbol, contextMarketData, snapshotData, marketData, currentBtcPrice]);

  const wsGetPrice = useCallback((symbol: string): number => {
    const bybitPrice = getBybitPrice(symbol);
    if (bybitPrice > 0) return bybitPrice;
    const snapshotPrice = getSnapshotPriceBySymbol(symbol);
    if (snapshotPrice > 0) return snapshotPrice;
    const md = contextMarketData.find(item => item.symbol === symbol);
    return md?.price || 0;
  }, [getBybitPrice, getSnapshotPriceBySymbol, contextMarketData]);

  const walletBreakdownData = useWalletBreakdown(
    usdtBalance,
    btcBalance,
    actualBtcPrice,
    wsGetPrice
  );
  
  // State for bank transfer details
  const [bankDetails, setBankDetails] = useState<any>(null);
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Withdrawal limit for unverified users (200 USD)
  const UNVERIFIED_WITHDRAWAL_LIMIT = 200;

  // Get user ID from Supabase auth
  const [userId, setUserId] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    
    fetchUserId();
  }, []);

  // Fetch bank details when bank transfer tab is active
  const fetchBankDetails = useCallback(async () => {
    if (!userId) return;
    setLoadingBankDetails(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('client_bank_details')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 means "no rows found"
        throw error;
      }
      setBankDetails(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load bank details.');
      setBankDetails(null);
    } finally {
      setLoadingBankDetails(false);
    }
  }, [userId]);

  useEffect(() => {
    if (activeTab === 'deposit' && depositMethod === 'bank_transfer') {
      fetchBankDetails();
    }
  }, [activeTab, depositMethod, fetchBankDetails]);

  // Handle crypto withdrawal button click
  const handleCryptoWithdrawalClick = (currency: 'USDT' | 'BTC') => {
    setSelectedCryptoForWithdrawal(currency);
    setShowCryptoWithdrawalModal(true);
  };

  // Handle withdrawal submission
  const handleBankWithdrawalSubmit = async (amount: number, bankDetails: {
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    beneficiaryName: string;
  }) => {
    try {
      // Create transaction with withdrawal details using direct Supabase insert
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error: transactionError } = await supabase
        .from('transactions')
        .insert([{
          user_id: user.id,
          type: 'withdrawal',
          amount: -amount,
          description: `Bank withdrawal of ${amount} USDT to ${bankDetails.bankName} (Acc: ••••${bankDetails.accountNumber.slice(-4)})`,
          status: 'pending',
          withdrawal_details: {
            currency: 'USDT',
            amount: amount,
            bank_name: bankDetails.bankName,
            account_number: bankDetails.accountNumber,
            routing_number: bankDetails.routingNumber,
            beneficiary_name: bankDetails.beneficiaryName,
            withdrawal_type: 'bank',
            created_at: new Date().toISOString()
          }
        }]);

      if (transactionError) {
        throw new Error(`Failed to create withdrawal transaction: ${transactionError.message}`);
      }
      
      // Set success message
      setMessage({ type: 'success', text: 'Bank withdrawal initiated successfully' });
      
      // Close modal after successful withdrawal
      setShowBankWithdrawalModal(false);
    } catch (error) {
      console.error('Error processing bank withdrawal:', error);
      setMessage({ type: 'error', text: 'Withdrawal failed. Please try again.' });
    }
  };

  // Handle crypto withdrawal
  const handleCryptoWithdrawal = async (currency: 'USDT' | 'BTC', amount: number, address: string, network: string) => {
    try {
      // Check available balance for withdrawal
      const withdrawalValueUSD = currency === 'USDT' ? amount : amount * actualBtcPrice;

      if (walletBreakdownData && withdrawalValueUSD > walletBreakdownData.availableBalance) {
        throw new Error(`Insufficient available balance. Required: ${withdrawalValueUSD.toFixed(2)} USD, Available: ${walletBreakdownData.availableBalance.toFixed(2)} USD`);
      }
      
      // Create transaction with withdrawal details using direct Supabase insert
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error: transactionError } = await supabase
        .from('transactions')
        .insert([{
          user_id: user.id,
          type: 'withdrawal',
          amount: -amount,
          description: `${currency} withdrawal of ${amount} ${currency} to ${address.substring(0, 8)}...${address.substring(address.length - 8)}`,
          status: 'pending',
          withdrawal_details: {
            currency: currency,
            amount: amount,
            recipient_address: address,
            network: network,
            withdrawal_type: 'crypto',
            created_at: new Date().toISOString()
          }
        }]);

      if (transactionError) {
        throw new Error(`Failed to create withdrawal transaction: ${transactionError.message}`);
      }
      
      // Set success message
      setMessage({ type: 'success', text: 'Crypto withdrawal initiated successfully' });
      
      // Close modal after successful withdrawal
      setShowCryptoWithdrawalModal(false);
    } catch (error) {
      console.error('Error processing crypto withdrawal:', error);
      setMessage({ type: 'error', text: 'Withdrawal failed. Please try again.' });
    }
  };

  const totalStakedValue = userStakes.reduce((total, stake) => {
    if (stake.status !== 'active') return total;
    
    const stakedValue = stake.asset_symbol === 'USDT' 
      ? parseFloat(stake.staked_amount.toString())
      : parseFloat(stake.staked_amount.toString()) * (marketData.find(m => m.symbol === 'BTCUSDT')?.price || 0);
    
    return total + stakedValue;
  }, 0);

  // Calculate total earnings from staking
  const totalStakingEarnings = userStakes.reduce((total, stake) => {
    const earnings = calculateCurrentEarnings(stake);
    return total + earnings;
  }, 0);

  // Calculate 24h change
  const btc24hChange = marketData.find(m => m.symbol === 'BTCUSDT')?.change_24h || 0;
  const portfolioChange24h = (btcBalance * actualBtcPrice * (btc24hChange / 100));
  
  // Use the wallet breakdown data for consistent calculations
  const effectiveTotalPortfolioValue = walletBreakdownData.totalBalance;
  const effectiveAvailableBalance = walletBreakdownData.availableBalance;

  // Handle stake cancellation
  const handleCancelStake = async (stakeId: string) => {
    try {
      await cancelUserStake(stakeId);
    } catch (error) {
      console.error('Error cancelling stake:', error);
    }
  };

  // Calculate total available balance from all user assets
  const totalAvailableBalance = useMemo(() => {
    // Use the wallet breakdown available balance for consistency
    return walletBreakdownData?.availableBalance || 0;
  }, [walletBreakdownData?.availableBalance]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatCrypto = (amount: number, symbol: string) => {
    const decimals = symbol === 'BTC' ? 6 : 2;
    return `${amount.toFixed(decimals)} ${symbol}`;
  };

  const getTransactionTypeLabel = (type: string) => {
    switch (type) {
      case 'deposit': return t('wallet.transactionTypes.deposit');
      case 'withdrawal': return t('wallet.transactionTypes.withdrawal');
      case 'trade': return t('wallet.transactionTypes.trade');
      case 'robot_profit': return t('wallet.transactionTypes.robotProfit');
      case 'binary_trade': return t('wallet.transactionTypes.binaryTrade');
      case 'stake': return t('wallet.transactionTypes.stake');
      case 'staking_profit': return t('wallet.transactionTypes.stakingProfit');
      case 'staking_return': return t('wallet.transactionTypes.stakingReturn');
      case 'challenge_fee': return t('wallet.transactionTypes.challengeFee');
      case 'challenge_reward': return t('wallet.transactionTypes.challengeReward');
      default: return type;
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
      case 'nowpayments_deposit':
        return <ArrowDownLeft size={16} className="text-green-400" />;
      case 'withdrawal':
        return <ArrowUpRight size={16} className="text-red-400" />;
      case 'trade':
        return <TrendingUp size={16} className="text-purple-300" />;
      case 'robot_profit':
        return <TrendingUp size={16} className="text-purple-400" />;
      case 'binary_trade':
        return <TrendingUp size={16} className="text-orange-400" />;
      case 'stake':
        return <Layers size={16} className="text-indigo-400" />;
      case 'staking_profit':
        return <TrendingUp size={16} className="text-green-400" />;
      case 'staking_return':
        return <ArrowDownLeft size={16} className="text-purple-300" />;
      default:
        return <DollarSign size={16} className="text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'pending':
        return 'text-yellow-400';
      case 'failed':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={14} className="text-green-400" />;
      case 'pending':
        return <Clock size={14} className="text-yellow-400" />;
      case 'failed':
        return <AlertTriangle size={14} className="text-red-400" />;
      default:
        return <Clock size={14} className="text-slate-400" />;
    }
  };

  const homeBackgroundClass = 'bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.16),transparent_30%),radial-gradient(circle_at_top_right,rgba(168,85,247,0.2),transparent_32%),radial-gradient(circle_at_bottom_center,rgba(217,70,239,0.14),transparent_38%)]';
  const primaryCardClass = 'rounded-2xl border border-purple-500/30 bg-gradient-to-br from-blue-500/24 via-indigo-500/18 to-purple-500/16 shadow-2xl shadow-purple-500/10 backdrop-blur-sm';
  const secondaryCardClass = 'rounded-2xl border border-purple-500/35 bg-gradient-to-br from-blue-500/22 via-indigo-500/16 to-purple-500/18 shadow-2xl shadow-purple-500/15 backdrop-blur-sm';
  const innerCardClass = 'rounded-xl border border-purple-500/25 bg-gradient-to-br from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12';
  const innerCardHoverClass = 'transition-all duration-300 hover:border-purple-400/45 hover:from-indigo-500/16 hover:via-purple-500/16 hover:to-fuchsia-500/16';
  const headerIconClass = 'flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/25';
  const sectionIconClass = 'flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/25';
  const primaryActionButtonClass = 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:from-purple-500 hover:to-fuchsia-500';
  const secondaryActionButtonClass = 'border border-purple-500/30 bg-gradient-to-r from-indigo-500/18 via-purple-500/18 to-fuchsia-500/18 text-white shadow-lg shadow-purple-500/15 transition-all duration-300 hover:from-indigo-500/28 hover:via-purple-500/28 hover:to-fuchsia-500/28 hover:border-purple-400/50';
  const neutralActionButtonClass = 'border border-purple-500/25 bg-purple-950/45 text-slate-200 transition-all duration-300 hover:bg-purple-500/12 hover:border-purple-400/40 hover:text-white';
  const activeControlClass = 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-purple-500/25 transform scale-105';
  const inactiveControlClass = 'border border-purple-500/25 bg-purple-950/45 text-slate-300 transition-all duration-300 hover:bg-purple-500/10 hover:text-white hover:border-purple-400/40';
  const inputClass = 'w-full rounded-xl border border-purple-500/30 bg-gradient-to-r from-indigo-950/72 via-purple-950/72 to-fuchsia-950/70 px-4 py-3 text-white placeholder:text-slate-400 transition-all focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/55';
  const inputSuffixClass = 'absolute right-3 top-1/2 -translate-y-1/2 rounded-lg border border-purple-500/20 bg-purple-950/55 px-2 py-1 text-sm text-slate-200';
  const tabRailClass = 'mb-8 flex flex-wrap gap-2 rounded-2xl border border-purple-500/25 bg-gradient-to-r from-indigo-500/12 via-purple-500/10 to-fuchsia-500/12 p-2 backdrop-blur-sm';
  const activeTabClass = 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-purple-500/25';
  const inactiveTabClass = 'border border-purple-500/20 text-slate-300 transition-all duration-300 hover:bg-purple-500/10 hover:border-purple-400/40 hover:text-white';
  const statCardClass = `${innerCardClass} ${innerCardHoverClass}`;
  const tintedInfoCardClass = 'rounded-xl border border-purple-500/25 bg-gradient-to-br from-indigo-500/10 via-purple-500/12 to-fuchsia-500/12';
  const subtleTextClass = 'text-slate-300';
  const mutedTextClass = 'text-slate-400';

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div className={`pointer-events-none absolute inset-0 ${homeBackgroundClass}`}></div>
      <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-indigo-500/18 blur-3xl"></div>
      <div className="pointer-events-none absolute right-0 top-32 h-80 w-80 rounded-full bg-purple-500/16 blur-3xl"></div>
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-fuchsia-500/10 blur-3xl"></div>
      {/* Message Display */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg border ${
          message.type === 'success' 
            ? 'bg-green-500/10 border-green-500/30 text-green-400' 
            : message.type === 'error'
            ? 'bg-red-500/10 border-red-500/30 text-red-400'
            : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' && <CheckCircle size={18} />}
            {message.type === 'error' && <AlertTriangle size={18} />}
            {message.type === 'warning' && <AlertTriangle size={18} />}
            <span className="font-medium">{message.text}</span>
          </div>
        </div>
      )}

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className={headerIconClass}>
              <Wallet size={24} className="text-white" />
            </div>
            <div>
              <h1 className="mb-2 bg-gradient-to-r from-purple-300 via-fuchsia-200 to-pink-300 bg-clip-text text-3xl font-bold text-transparent">
                {t('wallet.title')}
              </h1>
              <p className={subtleTextClass}>
                {t('wallet.subtitle')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/trading-fees')}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 ${secondaryActionButtonClass} text-purple-100`}
            >
              <Info size={18} className="text-purple-200" />
              <span className="text-sm">Trading Fees</span>
            </button>

            <button
              onClick={() => setShowBalance(!showBalance)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 ${neutralActionButtonClass}`}
            >
              {showBalance ? <Eye size={18} /> : <EyeOff size={18} />}
              <span className="text-sm">{showBalance ? 'Hide' : 'Show'} Balance</span>
            </button>
          </div>
        </div>

        {/* Portfolio Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Total Portfolio Value */}
          <div className={`lg:col-span-2 p-6 ${primaryCardClass}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${subtleTextClass}`}>Total Portfolio Value</h3>
              <div className="flex items-center gap-2">
                {portfolioChange24h >= 0 ? (
                  <TrendingUp size={18} className="text-green-400" />
                ) : (
                  <TrendingDown size={18} className="text-red-400" />
                )}
                <span className={`text-sm font-medium ${portfolioChange24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {portfolioChange24h >= 0 ? '+' : ''}{formatCurrency(portfolioChange24h)}
                </span>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="text-4xl font-bold text-white mb-2">
                {showBalance ? formatCurrency(walletBreakdownData?.totalBalance || 0) : '••••••'}
              </div>
              <div className={`text-sm ${mutedTextClass}`}>
                Available Balance: {showBalance ? formatCurrency(walletBreakdownData?.availableBalance || 0) : '••••••'}
              </div>
            </div>

            {/* Asset Breakdown */}
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 ${statCardClass}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/25">
                    <span className="text-xs font-bold text-white">$</span>
                  </div>
                  <span className={`${subtleTextClass} font-medium`}>USDT</span>
                </div>
                <div className="text-xl font-bold text-white">
                  {showBalance ? formatCrypto(usdtBalance, 'USDT') : '••••••'}
                </div>
                <div className={`text-sm ${mutedTextClass}`}>
                  {showBalance ? formatCurrency(usdtBalance) : '••••••'}
                </div>
                <div className="flex flex-col md:flex-row gap-3 mt-4">
                  <button
                    onClick={() => setActiveTab('deposit')}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-3 py-2 text-xs font-medium md:gap-2 md:text-sm ${primaryActionButtonClass}`}
                  >
                    <ArrowDownLeft size={16} />
                    Deposit
                  </button>
                  <button 
                    onClick={() => handleCryptoWithdrawalClick('USDT')}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-3 py-2 text-xs font-medium md:gap-2 md:text-sm ${secondaryActionButtonClass}`}>
                      <ArrowUpRight size={16} />
                      Withdraw
                    </button>
                </div>
              </div>

              <div className={`p-4 ${statCardClass}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 shadow-lg shadow-purple-500/25">
                    <Bitcoin size={16} className="text-white" />
                  </div>
                  <span className={`${subtleTextClass} font-medium`}>BTC</span>
                </div>
                <div className="text-xl font-bold text-white">
                  {showBalance ? formatCrypto(btcBalance, 'BTC') : '••••••'}
                </div>
                <div className={`text-sm ${mutedTextClass}`}>
                  {showBalance ? formatCurrency(btcBalance * actualBtcPrice) : '••••••'}
                </div>
                <div className="flex flex-col md:flex-row gap-3 mt-4">
                  <button
                    onClick={() => setActiveTab('deposit')}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-3 py-2 text-xs font-medium md:gap-2 md:text-sm ${primaryActionButtonClass}`}
                  >
                    <ArrowDownLeft size={16} />
                    Deposit
                  </button>
                  <button 
                    onClick={() => handleCryptoWithdrawalClick('BTC')}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-3 py-2 text-xs font-medium md:gap-2 md:text-sm ${secondaryActionButtonClass}`}>
                      <ArrowUpRight size={16} />
                      Withdraw
                    </button>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Breakdown */}
          <div className="space-y-6">
            {walletBreakdownData && !walletBreakdownData.loading ? (
              <WalletBreakdownCard
                totalBalance={walletBreakdownData.totalBalance}
                usedMargin={walletBreakdownData.usedMargin}
                futuresUsedMargin={walletBreakdownData.futuresUsedMargin}
                propUsedMargin={walletBreakdownData.propUsedMargin}
                futuresOrdersReserved={walletBreakdownData.futuresOrdersReserved}
                propOrdersReserved={walletBreakdownData.propOrdersReserved}
                unrealizedPnl={walletBreakdownData.unrealizedPnl}
                availableBalance={walletBreakdownData.availableBalance}
                robotAllocatedBalance={walletBreakdownData.robotAllocatedBalance}
                stakedAmount={walletBreakdownData.stakedAmount}
                loading={walletBreakdownData.loading}
              />
            ) : (
              <div className={`p-6 ${secondaryCardClass}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={sectionIconClass}>
                    <Wallet size={20} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Wallet Breakdown</h3>
                </div>
                <div className="animate-pulse space-y-3">
                  <div className="h-4 w-3/4 rounded bg-purple-500/20"></div>
                  <div className="h-4 w-1/2 rounded bg-purple-500/18"></div>
                  <div className="h-4 w-2/3 rounded bg-purple-500/16"></div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className={`p-6 ${secondaryCardClass}`}>
              <h3 className="text-lg font-semibold text-white mb-6">Quick Actions</h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => setActiveTab('deposit')}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-medium ${primaryActionButtonClass}`}
                >
                  <ArrowDownLeft size={18} />
                  Deposit
                </button>
                
                <button
                  onClick={() => setShowBankWithdrawalModal(true)}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-medium ${secondaryActionButtonClass}`}
                >
                  <Landmark size={16} />
                  Bank Withdrawal
                </button>
                
                <button
                  onClick={() => setTradingMode && setTradingMode('staking')}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-medium ${primaryActionButtonClass}`}
                >
                  <Layers size={18} />
                  Staking
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className={tabRailClass}>
          {[
            { id: 'overview', label: 'Overview', icon: Wallet },
            { id: 'deposit', label: 'Deposit', icon: ArrowDownLeft },
            { id: 'transactions', label: 'Transactions', icon: Clock },
            { id: 'staking', label: 'Staking', icon: Layers },
            { id: 'holdings', label: 'Holdings', icon: TrendingUp },
            { id: 'pnl', label: 'PnL Statement', icon: BarChart3 }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                activeTab === id
                  ? activeTabClass
                  : inactiveTabClass
              }`}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Transactions */}
            <div className={`p-6 ${primaryCardClass}`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Recent Transactions</h3>
                <button
                  onClick={() => setActiveTab('transactions')}
                  className="flex items-center gap-1 text-sm font-medium text-purple-300 transition-colors hover:text-purple-200"
                >
                  View All
                  <ArrowRight size={14} />
                </button>
              </div>
              
              <div className="space-y-3">
                {transactions.slice(0, 5).map((transaction) => (
                  <div key={transaction.id} className={`flex items-center justify-between p-3 ${statCardClass}`}>
                    <div className="flex items-center gap-3">
                      {getTransactionIcon(transaction.type)}
                      <div>
                        <div className="text-white font-medium text-sm">
                          {transaction.description}
                        </div>
                        <div className={`text-xs ${mutedTextClass}`}>
                          {formatDate(transaction.created_at || transaction.timestamp)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-medium text-sm ${
                        transaction.type === 'deposit' || transaction.type === 'nowpayments_deposit' || transaction.type === 'robot_profit' || transaction.type === 'staking_profit'
                          ? 'text-green-400' 
                          : transaction.type === 'withdrawal'
                          ? 'text-red-400'
                          : 'text-white'
                      }`}>
                        {transaction.type === 'deposit' || transaction.type === 'nowpayments_deposit' || transaction.type === 'robot_profit' || transaction.type === 'staking_profit' ? '+' : ''}
                        {formatCurrency(parseFloat(transaction.amount.toString()))}
                      </div>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(transaction.status)}
                        <span className={`text-xs ${getStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {transactions.length === 0 && (
                  <div className={`py-8 text-center ${mutedTextClass}`}>
                    <Clock size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No transactions yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Portfolio Performance */}
            <div className={`p-6 ${primaryCardClass}`}>
              <h3 className="text-xl font-semibold text-white mb-6">Portfolio Performance</h3>
              
              <div className="space-y-4">
                <div className={`flex justify-between items-center p-4 ${statCardClass}`}>
                  <span className={subtleTextClass}>Total Value</span>
                  <span className="text-white font-bold">
                    {showBalance ? formatCurrency(walletBreakdownData?.totalBalance || 0) : '••••••'}
                  </span>
                </div>
                
                <div className={`flex justify-between items-center p-4 ${statCardClass}`}>
                  <span className={subtleTextClass}>Available Balance</span>
                  <span className="text-white font-bold">
                    {showBalance ? formatCurrency(walletBreakdownData?.availableBalance || 0) : '••••••'}
                  </span>
                </div>
                
                <div className={`flex justify-between items-center p-4 ${statCardClass}`}>
                  <span className={subtleTextClass}>Staked Assets</span>
                  <span className="text-white font-bold">
                    {showBalance ? formatCurrency(walletBreakdownData.stakedAmount) : '••••••'}
                  </span>
                </div>
                
                <div className={`flex justify-between items-center p-4 ${statCardClass}`}>
                  <span className={subtleTextClass}>Staking Earnings</span>
                  <span className="text-green-400 font-bold">
                    {showBalance ? `+${formatCurrency(totalStakingEarnings)}` : '••••••'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'deposit' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className={`p-8 ${primaryCardClass}`}>
                <div className="flex items-center gap-3 mb-8">
                  <div className={sectionIconClass}>
                    <ArrowDownLeft size={20} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Deposit Funds</h3>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className={`mb-3 block text-sm ${mutedTextClass}`}>Select Currency</label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setSelectedCurrency('USDT')}
                        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          selectedCurrency === 'USDT' && depositMethod === 'bank_transfer'
                            ? activeControlClass
                            : inactiveControlClass
                        }`}
                      >
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500">
                          <span className="text-xs font-bold text-white">$</span>
                        </div>
                        USDT
                      </button>
                      <button
                        onClick={() => setSelectedCurrency('BTC')}
                        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          selectedCurrency === 'BTC' && depositMethod === 'bank_transfer'
                            ? activeControlClass
                            : inactiveControlClass
                        }`}
                      >
                        <Bitcoin size={18} className={selectedCurrency === 'BTC' && depositMethod === 'bank_transfer' ? 'text-white' : 'text-purple-200'} />
                        BTC
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className={`mb-2 flex justify-between text-sm ${mutedTextClass}`}>
                      <span>Amount</span>
                      <span>Min: $10</span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        placeholder={`Enter ${selectedCurrency} amount`}
                        className={inputClass}
                      />
                      <div className={inputSuffixClass}>
                        {selectedCurrency}
                      </div>
                    </div>
                  </div>

                  {/* Deposit Method Selector */}
                  <div className="mb-6">
                    <label className={`mb-3 block text-sm ${mutedTextClass}`}>Select Deposit Method</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setDepositMethod('nowpayments')}
                        className={`py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          depositMethod === 'nowpayments'
                            ? activeControlClass
                            : inactiveControlClass
                        }`}
                      >
                        <Card size={18} className={depositMethod === 'nowpayments' ? 'text-white' : 'text-purple-200'} />
                        Crypto
                      </button>
                      <button
                        onClick={() => setDepositMethod('bank_transfer')}
                        className={`py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          depositMethod === 'bank_transfer'
                            ? activeControlClass
                            : inactiveControlClass
                        }`}
                      >
                        <Banknote size={18} className={depositMethod === 'bank_transfer' ? 'text-white' : 'text-purple-200'} />
                        Bank
                      </button>
                    </div>
                  </div>

                  {depositMethod === 'nowpayments' ? (
                    userId ? (
                      <NowPaymentsDeposit
                        userId={userId}
                        onSuccess={() => {
                          setDepositMethod('bank_transfer');
                        }}
                      />
                    ) : (
                      <div className={`py-8 text-center ${mutedTextClass}`}>
                        <p>Loading user information...</p>
                      </div>
                    )
                  ) : (
                    <div className="space-y-6">
                      <div className={`p-6 ${tintedInfoCardClass}`}>
                        <h3 className="text-lg font-semibold text-white mb-4">Bank Transfer Details</h3>
                        {loadingBankDetails ? (
                          <div className={`py-8 text-center ${mutedTextClass}`}>
                            <div className="mx-auto mb-2 h-8 w-8 rounded-full border-2 border-purple-400 border-t-transparent animate-spin"></div>
                            Loading bank details...
                          </div>
                        ) : error ? (
                          <div className="text-center py-8 text-red-400">
                            <AlertTriangle size={24} className="mx-auto mb-2" />
                            {error}
                          </div>
                        ) : bankDetails ? (
                          <div className="space-y-3">
                            <div>
                              <div className={`text-sm ${mutedTextClass}`}>Bank Name</div>
                              <div className="text-white font-medium">{bankDetails.bank_name || 'Not Yet Available'}</div>
                            </div>
                            <div>
                              <div className={`text-sm ${mutedTextClass}`}>Account Number</div>
                              <div className="text-white font-medium">{bankDetails.account_number || 'Not Yet Available'}</div>
                            </div>
                            <div>
                              <div className={`text-sm ${mutedTextClass}`}>Routing Number</div>
                              <div className="text-white font-medium">{bankDetails.routing_number || 'Not Yet Available'}</div>
                            </div>
                            <div>
                              <div className={`text-sm ${mutedTextClass}`}>SWIFT/BIC Code</div>
                              <div className="text-white font-medium">{bankDetails.swift_code || 'Not Yet Available'}</div>
                            </div>
                            <div>
                              <div className={`text-sm ${mutedTextClass}`}>Beneficiary Name</div>
                              <div className="text-white font-medium">{bankDetails.beneficiary_name || 'Not Yet Available'}</div>
                            </div>
                          </div>
                        ) : (
                          <div className={`py-8 text-center ${mutedTextClass}`}>
                            <Banknote size={48} className="mx-auto mb-4 opacity-50" />
                            <p className="mb-2">Bank transfer details are not yet available for your account.</p>
                            <p className="text-sm">Please contact support for assistance.</p>
                          </div>
                        )}
                      </div>

                      <button
                        disabled={true}
                        className="w-full cursor-not-allowed rounded-xl border border-purple-500/20 bg-purple-950/35 py-3 font-medium text-slate-500"
                      >
                        Deposit via Bank Transfer (Not Yet Available)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className={`p-6 ${secondaryCardClass}`}>
              <div className="flex items-start gap-3">
                <Info size={20} className="text-purple-300 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">About Deposits</h3>
                  <p className={`mb-4 text-sm ${subtleTextClass}`}>
                    All deposits are processed securely. For bank transfers, please ensure the beneficiary name matches your registered account name to avoid delays.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className={`p-3 ${tintedInfoCardClass}`}>
                      <div className={`mb-1 ${mutedTextClass}`}>Processing Time</div>
                      <ul className={`${subtleTextClass} space-y-1`}>
                        <li>• Crypto: Instant (after confirmations)</li>
                        <li>• Bank Transfer: 1-3 business days</li>
                      </ul>
                    </div>
                    <div className={`p-3 ${tintedInfoCardClass}`}>
                      <div className={`mb-1 ${mutedTextClass}`}>Fees</div>
                      <ul className={`${subtleTextClass} space-y-1`}>
                        <li>• Crypto: Network fees apply</li>
                        <li>• Bank Transfer: No platform fees</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className={`p-8 ${primaryCardClass}`}>
            <div className="flex items-center gap-3 mb-8">
              <div className={sectionIconClass}>
                <Clock size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white">Transaction History</h3>
            </div>

            <div className="space-y-3">
              {transactions.map((transaction) => (
                <div key={transaction.id} className={`flex items-center justify-between p-4 ${statCardClass}`}>
                  <div className="flex items-center gap-4">
                    {getTransactionIcon(transaction.type)}
                    <div>
                      <div className="text-white font-medium">
                        {transaction.description}
                      </div>
                      <div className={`text-sm ${mutedTextClass}`}>
                        {new Date(transaction.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-bold ${
                      transaction.type === 'deposit' || transaction.type === 'nowpayments_deposit' || transaction.type === 'robot_profit' || transaction.type === 'staking_profit'
                        ? 'text-green-400' 
                        : transaction.type === 'withdrawal'
                        ? 'text-red-400'
                        : 'text-white'
                    }`}>
                      {transaction.type === 'deposit' || transaction.type === 'nowpayments_deposit' || transaction.type === 'robot_profit' || transaction.type === 'staking_profit' ? '+' : ''}
                      {formatCurrency(parseFloat(transaction.amount.toString()))}
                    </div>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(transaction.status)}
                      <span className={`text-sm ${getStatusColor(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              
              {transactions.length === 0 && (
                <div className={`py-12 text-center ${mutedTextClass}`}>
                  <Clock size={64} className="mx-auto mb-4 opacity-50" />
                  <h4 className="text-xl font-medium mb-2">No transactions yet</h4>
                  <p>Your transaction history will appear here</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'staking' && (
          <div className="space-y-8">
            <div className={`p-6 ${primaryCardClass}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className={sectionIconClass}>
                  <Layers size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white">Active Stakes</h3>
              </div>

              <div className="space-y-4">
                {userStakes.filter(stake => stake.status === 'active').map((stake) => {
                  const currentEarnings = calculateCurrentEarnings(stake);
                  const progress = ((Date.now() - new Date(stake.start_date).getTime()) / (new Date(stake.end_date).getTime() - new Date(stake.start_date).getTime())) * 100;
                  
                  return (
                    <div key={stake.id} className={`p-4 ${statCardClass}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {stake.asset_symbol === 'BTC' ? (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 shadow-lg shadow-purple-500/25">
                              <Bitcoin size={16} className="text-white" />
                            </div>
                          ) : (
                            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500">
                              <span className="text-xs font-bold text-white">$</span>
                            </div>
                          )}
                          <span className="text-white font-medium">{stake.asset_symbol}</span>
                        </div>
                        <span className="text-green-400 font-bold text-sm">
                          {stake.apy_rate}% APY
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <div className={`text-xs ${mutedTextClass}`}>Staked Amount</div>
                          <div className="text-white font-medium">
                            {formatCrypto(parseFloat(stake.staked_amount.toString()), stake.asset_symbol)}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs ${mutedTextClass}`}>Current Earnings</div>
                          <div className="text-green-400 font-medium">
                            +{formatCrypto(currentEarnings, stake.asset_symbol)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className={`mb-1 flex justify-between text-xs ${mutedTextClass}`}>
                          <span>Progress</span>
                          <span>{Math.min(progress, 100).toFixed(1)}%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-purple-950/60">
                          <div 
                            className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all duration-300"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className={`flex items-center justify-between text-xs ${mutedTextClass}`}>
                        <span>End Date: {new Date(stake.end_date).toLocaleDateString()}</span>
                        <button
                          onClick={() => handleCancelStake(stake.id)}
                          className="font-medium text-purple-300 transition-colors hover:text-purple-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })}
                
                {userStakes.filter(stake => stake.status === 'active').length === 0 && (
                  <div className={`py-8 text-center ${mutedTextClass}`}>
                    <Layers size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No active stakes</p>
                    <p className="text-sm">Start staking to earn passive income</p>
                  </div>
                )}
              </div>
            </div>

            {/* Staking Summary */}
            <div className={`p-6 ${secondaryCardClass}`}>
              <h3 className="text-xl font-semibold text-white mb-6">Staking Summary</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 ${statCardClass}`}>
                  <div className={`mb-1 text-sm ${mutedTextClass}`}>Total Staked Value</div>
                  <div className="text-2xl font-bold text-white">
                    {showBalance ? formatCurrency(totalStakedValue) : '••••••'}
                  </div>
                </div>
                
                <div className={`p-4 ${statCardClass}`}>
                  <div className={`mb-1 text-sm ${mutedTextClass}`}>Total Earnings</div>
                  <div className="text-2xl font-bold text-green-400">
                    {showBalance ? `+${formatCurrency(totalStakingEarnings)}` : '••••••'}
                  </div>
                </div>
                
                <div className={`p-4 ${statCardClass}`}>
                  <div className={`mb-1 text-sm ${mutedTextClass}`}>Active Stakes</div>
                  <div className="text-2xl font-bold text-white">
                    {userStakes.filter(stake => stake.status === 'active').length}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => setTradingMode && setTradingMode('staking')}
                className={`mt-6 w-full rounded-xl py-3 font-medium ${primaryActionButtonClass}`}
              >
                Start New Stake
              </button>
            </div>
          </div>
        )}

        {activeTab === 'holdings' && (
          <CryptoHoldings
            usdtBalance={usdtBalance}
            btcBalance={btcBalance}
            currentBtcPrice={actualBtcPrice}
            marketData={marketData}
            showBalance={showBalance}
          />
        )}

        {activeTab === 'pnl' && (
          <PnlStatement />
        )}
      </div>

      {/* Withdrawal Modal */}
      <BankWithdrawalModal 
        isOpen={showBankWithdrawalModal}
        onClose={() => setShowBankWithdrawalModal(false)}
        usdtBalance={walletBreakdownData?.availableBalance || 0}
        onWithdraw={handleBankWithdrawalSubmit} 
      />

      {/* Crypto Withdrawal Modal */}
      <CryptoWithdrawalModal
        isOpen={showCryptoWithdrawalModal}
        onClose={() => setShowCryptoWithdrawalModal(false)}
        usdtBalance={walletBreakdownData?.availableBalance || 0}
        btcBalance={btcBalance}
        currentBtcPrice={actualBtcPrice}
        initialCurrency={selectedCryptoForWithdrawal}
        onWithdraw={handleCryptoWithdrawal}
      />
    </div>
  );
};

export default WalletPage;
